import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import {
  RESIDENT_COLUMN_VISIBILITY_STORAGE_KEY,
  RESIDENT_LIST_COLUMN_OPTIONS,
  RESIDENT_LIST_CUSTOMIZABLE_COLUMN_OPTIONS,
  RESIDENT_LIST_DATA_COLUMN_IDS,
  RESIDENT_LIST_LOCKED_COLUMN_IDS,
  withLockedResidentListColumns,
  type ResidentColumnSettings,
  type ResidentListColumnId,
} from '../utils/residentListColumns'

const DEFAULT_RESIDENT_COLUMN_IDS = [
  ...RESIDENT_LIST_DATA_COLUMN_IDS,
] as ResidentListColumnId[]

const DEFAULT_CUSTOMIZABLE_COLUMN_IDS =
  RESIDENT_LIST_CUSTOMIZABLE_COLUMN_OPTIONS.map((column) => column.id)

function normalizeColumnIds(
  columnIds: unknown,
): ResidentListColumnId[] {
  const known = new Set<string>(RESIDENT_LIST_DATA_COLUMN_IDS)
  const ordered: ResidentListColumnId[] = []
  if (Array.isArray(columnIds)) {
    for (const rawId of columnIds) {
      if (typeof rawId !== 'string') continue
      if (known.has(rawId) && !ordered.includes(rawId as ResidentListColumnId)) {
        ordered.push(rawId as ResidentListColumnId)
      }
    }
  }
  for (const id of DEFAULT_RESIDENT_COLUMN_IDS) {
    if (!ordered.includes(id)) ordered.push(id)
  }
  return withLockedResidentListColumns(ordered)
}

function loadResidentColumnSettings(): ResidentColumnSettings {
  const fallback: ResidentColumnSettings = {
    visibleColumnIds: DEFAULT_RESIDENT_COLUMN_IDS,
    columnOrderIds: DEFAULT_RESIDENT_COLUMN_IDS,
  }
  try {
    const raw = localStorage.getItem(RESIDENT_COLUMN_VISIBILITY_STORAGE_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<ResidentColumnSettings>
    return {
      visibleColumnIds: withLockedResidentListColumns(
        normalizeColumnIds(parsed.visibleColumnIds).filter((columnId) =>
          parsed.visibleColumnIds?.includes(columnId),
        ),
      ),
      columnOrderIds: normalizeColumnIds(parsed.columnOrderIds),
    }
  } catch {
    return fallback
  }
}

function reorderColumnIds(
  columnIds: ResidentListColumnId[],
  draggedId: ResidentListColumnId,
  targetId: ResidentListColumnId,
): ResidentListColumnId[] {
  if (draggedId === targetId) return columnIds
  const next = columnIds.filter((id) => id !== draggedId)
  const targetIndex = next.indexOf(targetId)
  if (targetIndex < 0) return columnIds
  next.splice(targetIndex, 0, draggedId)
  return withLockedResidentListColumns(next)
}

export function useResidentListColumns() {
  const [isColumnsOpen, setIsColumnsOpen] = useState(false)
  const [columnSearchTerm, setColumnSearchTerm] = useState('')
  const [columnSettings, setColumnSettings] = useState<ResidentColumnSettings>(
    loadResidentColumnSettings,
  )
  const [draggedColumnId, setDraggedColumnId] = useState<ResidentListColumnId | null>(
    null,
  )
  const [dragPreviewColumnOrderIds, setDragPreviewColumnOrderIds] = useState<
    ResidentListColumnId[] | null
  >(null)

  const columnsMenuRef = useRef<HTMLDivElement | null>(null)
  const columnsListRef = useRef<HTMLDivElement | null>(null)

  const { visibleColumnIds, columnOrderIds } = columnSettings
  const dropdownColumnOrderIds = dragPreviewColumnOrderIds ?? columnOrderIds

  useEffect(() => {
    localStorage.setItem(
      RESIDENT_COLUMN_VISIBILITY_STORAGE_KEY,
      JSON.stringify(columnSettings),
    )
  }, [columnSettings])

  useEffect(() => {
    if (!isColumnsOpen) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (target && columnsMenuRef.current?.contains(target)) return
      setIsColumnsOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsColumnsOpen(false)
    }
    // Use bubble-phase click (not mousedown) so the Columns button toggle
    // is not cancelled by an outside-dismiss that races the open click.
    document.addEventListener('click', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('click', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isColumnsOpen])

  const availableColumnOptions = RESIDENT_LIST_CUSTOMIZABLE_COLUMN_OPTIONS
  const visibleColumnSet = useMemo(
    () => new Set<ResidentListColumnId>(visibleColumnIds),
    [visibleColumnIds],
  )
  const selectedAvailableColumnCount = availableColumnOptions.filter((column) =>
    visibleColumnSet.has(column.id),
  ).length
  const areAllAvailableColumnsSelected =
    selectedAvailableColumnCount === availableColumnOptions.length
  const areNoAvailableColumnsSelected = selectedAvailableColumnCount === 0

  const filteredColumnOptions = useMemo(() => {
    const needle = columnSearchTerm.trim().toLowerCase()
    const orderedOptions = dropdownColumnOrderIds
      .map((id) => RESIDENT_LIST_COLUMN_OPTIONS.find((column) => column.id === id))
      .filter((column): column is (typeof RESIDENT_LIST_COLUMN_OPTIONS)[number] =>
        Boolean(column && column.id !== 'applicant_id'),
      )
    if (!needle) return orderedOptions
    return orderedOptions.filter(
      (column) =>
        column.label.toLowerCase().includes(needle) ||
        column.id.toLowerCase().includes(needle),
    )
  }, [columnSearchTerm, dropdownColumnOrderIds])

  const setVisibleColumns = useCallback((columnIds: ResidentListColumnId[]) => {
    setColumnSettings((currentSettings) => ({
      ...currentSettings,
      visibleColumnIds: withLockedResidentListColumns(
        currentSettings.columnOrderIds.filter((columnId) =>
          columnIds.includes(columnId),
        ),
      ),
    }))
  }, [])

  const toggleColumn = useCallback((columnId: ResidentListColumnId) => {
    setColumnSettings((currentSettings) => {
      const nextColumnIds = currentSettings.visibleColumnIds.includes(columnId)
        ? currentSettings.visibleColumnIds.filter((id) => id !== columnId)
        : [...currentSettings.visibleColumnIds, columnId]
      return {
        ...currentSettings,
        visibleColumnIds: withLockedResidentListColumns(nextColumnIds),
      }
    })
  }, [])

  const handleColumnDragStart = useCallback(
    (event: DragEvent, columnId: ResidentListColumnId) => {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', columnId)
      setDraggedColumnId(columnId)
      setDragPreviewColumnOrderIds(columnOrderIds)
    },
    [columnOrderIds],
  )

  const autoScrollColumnsList = useCallback((clientY: number) => {
    const list = columnsListRef.current
    if (!list) return
    const rect = list.getBoundingClientRect()
    const edge = 36
    if (clientY < rect.top + edge) list.scrollTop -= 12
    else if (clientY > rect.bottom - edge) list.scrollTop += 12
  }, [])

  const handleColumnDragOver = useCallback(
    (event: DragEvent, targetId: ResidentListColumnId) => {
      event.preventDefault()
      autoScrollColumnsList(event.clientY)
      if (!draggedColumnId) return
      setDragPreviewColumnOrderIds((currentOrderIds) =>
        reorderColumnIds(currentOrderIds ?? columnOrderIds, draggedColumnId, targetId),
      )
    },
    [autoScrollColumnsList, columnOrderIds, draggedColumnId],
  )

  const handleColumnDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      if (!dragPreviewColumnOrderIds) {
        setDraggedColumnId(null)
        return
      }
      setColumnSettings((currentSettings) => ({
        ...currentSettings,
        columnOrderIds: dragPreviewColumnOrderIds,
      }))
      setDraggedColumnId(null)
      setDragPreviewColumnOrderIds(null)
    },
    [dragPreviewColumnOrderIds],
  )

  const resetColumnsToDefault = useCallback(() => {
    setColumnSettings({
      visibleColumnIds: DEFAULT_RESIDENT_COLUMN_IDS,
      columnOrderIds: DEFAULT_RESIDENT_COLUMN_IDS,
    })
  }, [])

  return {
    isColumnsOpen,
    setIsColumnsOpen,
    columnSearchTerm,
    setColumnSearchTerm,
    visibleColumnIds,
    columnOrderIds,
    columnsMenuRef,
    columnsListRef,
    availableColumnOptions,
    filteredColumnOptions,
    selectedAvailableColumnCount,
    areAllAvailableColumnsSelected,
    areNoAvailableColumnsSelected,
    visibleColumnSet,
    draggedColumnId,
    setDraggedColumnId,
    setDragPreviewColumnOrderIds,
    setVisibleColumns,
    toggleColumn,
    handleColumnDragStart,
    handleColumnDragOver,
    handleColumnDrop,
    autoScrollColumnsList,
    resetColumnsToDefault,
    DEFAULT_CUSTOMIZABLE_COLUMN_IDS,
    RESIDENT_LIST_LOCKED_COLUMN_IDS,
  }
}
